/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2024-03-14 00:00:00
 *
 *******************************************************/
import React, { Component } from 'react';
import { TextStyle, Button, Banner, ButtonGroup } from '@shopify/polaris';
import { DuplicateMinor } from '@shopify/polaris-icons';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';

import { routes } from 'src/routes';

import { getShop, buildRedirectUrl, getThemeReviewUrl } from 'src/helpers';
import { SSR_APP_BLOCK } from 'src/constants/shopify-integration';
import {
  navigateToAppBlockSSR,
  copyClipBoard,
  navigateToThemeCodeEditor
} from 'src/helpers/shopify-integration-helpers';

import GuideCollectionPageStep1 from 'src/assets/images/shopify-integration/GuideCollectionPageStep1.png';
import GuideCollectionPageStep2 from 'src/assets/images/shopify-integration/GuideCollectionPageStep2.png';
import GuideSearchPageStep1 from 'src/assets/images/shopify-integration/GuideSearchPageStep1.png';
import GuideSearchPageStep2 from 'src/assets/images/shopify-integration/GuideSearchPageStep2.png';

import GuideCollectionPageOS10Step1 from 'src/assets/images/shopify-integration/GuideCollectionPageOS10Step1.gif';
import GuideCollectionPageOS10Step2 from 'src/assets/images/shopify-integration/GuideCollectionPageOS10Step2.gif';
import GuideSearchPageOS10Step1 from 'src/assets/images/shopify-integration/GuideSearchPageOS10Step1.gif';
import GuideSearchPageOS10Step2 from 'src/assets/images/shopify-integration/GuideSearchPageOS10Step2.gif';

type Props = {};

type ComposedProps = Props & WithI18nProps;

class CollectionSearchManual extends Component<ComposedProps> {
  state = {};

  render() {
    const { i18n, pageType, themeId, isOS20 = true } = this.props;

    const heading =
      pageType === 'collection'
        ? i18n.translate('AdminHTML.ShopifyIntegration.Guide.menuCollectionPage')
        : i18n.translate('AdminHTML.ShopifyIntegration.Guide.menuSearchPage');

    const step3Instruction =
      pageType === 'collection'
        ? i18n.translate('AdminHTML.ShopifyIntegration.Guide.collectionPageStep3_instruction', {
            t1: (
              <TextStyle variation="strong">
                {i18n.translate('AdminHTML.ShopifyIntegration.Actions.preview')}
              </TextStyle>
            )
          })
        : i18n.translate('AdminHTML.ShopifyIntegration.Guide.searchPageStep3_instruction', {
            t1: (
              <TextStyle variation="strong">
                {i18n.translate('AdminHTML.ShopifyIntegration.Actions.preview')}
              </TextStyle>
            )
          });

    return (
      <React.Fragment>
        <div className="heading">{heading}</div>

        {/* Banner warning */}
        {isOS20 ? this.renderWarningBannerOS20() : this.renderWarningBannerOS10()}

        <div className="instruction">
          {isOS20 ? this.renderContentOS20() : this.renderContentOS10()}

          {/* Step 3 */}
          <div className="step">
            <div style={{ display: 'inline-flex' }}>
              <div className="left-col" style={{ width: '100%' }}>
                <TextStyle variation="strong">
                  {i18n.translate('AdminHTML.ShopifyIntegration.Guide.collectionPageStep3')}
                </TextStyle>

                <div style={{ padding: '16px 0' }}>{step3Instruction}</div>
                <div className="btnGroup">
                  <ButtonGroup>
                    {/* Change template */}
                    <Button
                      onClick={() => {
                        const previewUrl = getThemeReviewUrl(themeId);
                        window.open(previewUrl, '_blank');
                      }}>
                      {i18n.translate('AdminHTML.ShopifyIntegration.Actions.preview')}
                    </Button>

                    {/* Customization */}
                    <Button
                      onClick={() => {
                        const redirectUrl = buildRedirectUrl(getShop(), routes.filterTree.list());
                        window.open(redirectUrl, '_blank');
                      }}>
                      {i18n.translate('AdminHTML.ShopifyIntegration.Actions.gotoFilterSettings')}
                    </Button>
                  </ButtonGroup>
                </div>
              </div>
            </div>
          </div>
        </div>
      </React.Fragment>
    );
  }

  renderWarningBannerOS10 = () => {
    const { i18n, backToMainStep } = this.props;

    return (
      <div className="warning">
        <Banner status="critical">
          {i18n.translate('AdminHTML.ShopifyIntegration.Guide.warningInitialSetup', {
            t1: (
              <Button plain monochrome onClick={backToMainStep}>
                <TextStyle variation="strong">
                  {i18n.translate('AdminHTML.ShopifyIntegration.Guide.warningInitialSetup_t1')}
                </TextStyle>
              </Button>
            )
          })}
        </Banner>
      </div>
    );
  };

  renderWarningBannerOS20 = () => {
    const { i18n, backToMainStep } = this.props;

    return (
      <div className="warning">
        <Banner status="critical">
          {i18n.translate('AdminHTML.ShopifyIntegration.Guide.warning', {
            t1: (
              <Button plain monochrome onClick={backToMainStep}>
                <TextStyle variation="strong">
                  {i18n.translate('AdminHTML.ShopifyIntegration.Guide.warning_t1')}
                </TextStyle>
              </Button>
            ),
            t2: (
              <TextStyle variation="strong">
                {i18n.translate('AdminHTML.ShopifyIntegration.Guide.warning_t2')}
              </TextStyle>
            )
          })}
        </Banner>
      </div>
    );
  };

  renderContentOS10 = () => {
    const { i18n, themeId, pageType } = this.props;

    return (
      <React.Fragment>
        {/* Step 1 */}
        <div className="step">
          <div style={{ display: 'inline-flex' }}>
            <div className="left-col">
              <TextStyle variation="strong">
                {pageType === 'collection'
                  ? i18n.translate('AdminHTML.ShopifyIntegration.Guide.collectionPageOS10Step1')
                  : i18n.translate('AdminHTML.ShopifyIntegration.Guide.searchPageOS10Step1')}
              </TextStyle>

              <div style={{ padding: '16px 0 0 0' }}>
                {i18n.translate(
                  'AdminHTML.ShopifyIntegration.Guide.collectionPageOS10Step1_instruction1'
                )}
              </div>

              <div style={{ padding: '16px 0' }}>
                {i18n.translate(
                  'AdminHTML.ShopifyIntegration.Guide.collectionPageOS10Step1_instruction2',
                  {
                    t1: (
                      <TextStyle variation="strong">
                        {i18n.translate(
                          'AdminHTML.ShopifyIntegration.Guide.collectionPageOS10Step1_instruction2_t1'
                        )}
                      </TextStyle>
                    ),
                    t2: (
                      <span className="asset-file">
                        {pageType === 'collection'
                          ? i18n.translate(
                              'AdminHTML.ShopifyIntegration.Guide.collectionPageOS10Step1_instruction2_t2'
                            )
                          : i18n.translate(
                              'AdminHTML.ShopifyIntegration.Guide.searchPageOS10Step1_instruction2_t2'
                            )}
                      </span>
                    )
                  }
                )}
              </div>
              <div>
                <Button
                  onClick={() => {
                    navigateToThemeCodeEditor(themeId);
                  }}>
                  {i18n.translate('AdminHTML.ShopifyIntegration.Actions.gotoEditCode')}
                </Button>
              </div>
            </div>
            {/* Illustration image */}
            <div className="right-col">
              <img
                className="thumbnail clickable"
                src={
                  pageType === 'collection'
                    ? GuideCollectionPageOS10Step1
                    : GuideSearchPageOS10Step1
                }
                width={500}
                height={281}
                alt=""
                onClick={() =>
                  window.open(
                    pageType === 'collection'
                      ? GuideCollectionPageOS10Step1
                      : GuideSearchPageOS10Step1,
                    '_blank',
                    'noopener,noreferrer'
                  )
                }
              />
            </div>
          </div>
        </div>

        {/* Step 2 */}
        <div className="step">
          <div style={{ display: 'inline-flex' }}>
            <div className="left-col">
              <TextStyle variation="strong">
                {pageType === 'collection'
                  ? i18n.translate('AdminHTML.ShopifyIntegration.Guide.collectionPageOS10Step2')
                  : i18n.translate('AdminHTML.ShopifyIntegration.Guide.searchPageOS10Step2')}
              </TextStyle>

              <div style={{ padding: '16px 0' }}>
                {i18n.translate(
                  'AdminHTML.ShopifyIntegration.Guide.collectionPageOS10Step2_instruction1',
                  {
                    t1: (
                      <span className="asset-file">
                        {pageType === 'collection'
                          ? i18n.translate(
                              'AdminHTML.ShopifyIntegration.Guide.collectionPageOS10Step2_instruction1_t1'
                            )
                          : i18n.translate(
                              'AdminHTML.ShopifyIntegration.Guide.searchPageOS10Step2_instruction1_t1'
                            )}
                      </span>
                    )
                  }
                )}
              </div>

              <div className="code" style={{ fontSize: '12px' }}>
                <span className="copy custom-style">
                  <Button
                    icon={DuplicateMinor}
                    plain
                    onClick={() => this.copyCodeSnippetClipBoardStep1()}
                  />
                </span>
                <span>{`{% comment %}`}</span>
                <br />
                <span>{`Place original file code here`}</span>
                <br />
                <span>{`{% endcomment %}`}</span>
              </div>

              <div style={{ padding: '16px 0' }}>
                {i18n.translate(
                  'AdminHTML.ShopifyIntegration.Guide.collectionPageOS10Step2_instruction2',
                  {
                    t1: (
                      <span className="asset-file">
                        {pageType === 'collection'
                          ? i18n.translate(
                              'AdminHTML.ShopifyIntegration.Guide.collectionPageOS10Step2_instruction1_t1'
                            )
                          : i18n.translate(
                              'AdminHTML.ShopifyIntegration.Guide.searchPageOS10Step2_instruction1_t1'
                            )}
                      </span>
                    ),
                    t2: (
                      <TextStyle variation="strong">
                        {i18n.translate(
                          'AdminHTML.ShopifyIntegration.Guide.collectionPageOS10Step2_instruction2_t2'
                        )}
                      </TextStyle>
                    )
                  }
                )}
              </div>
            </div>

            {/* Illustration image */}
            <div className="right-col">
              <img
                className="thumbnail clickable"
                src={
                  pageType === 'collection'
                    ? GuideCollectionPageOS10Step2
                    : GuideSearchPageOS10Step2
                }
                width={500}
                height={281}
                alt=""
                onClick={() =>
                  window.open(
                    pageType === 'collection'
                      ? GuideCollectionPageOS10Step2
                      : GuideSearchPageOS10Step2,
                    '_blank',
                    'noopener,noreferrer'
                  )
                }
              />
            </div>
          </div>

          <div className="code" style={{ fontSize: '12px' }}>
            <span className="copy custom-style">
              <Button
                icon={DuplicateMinor}
                plain
                onClick={() => this.copyCodeSnippetClipBoardStep2()}
              />
            </span>
            <span>{`{% render 'boost-sd-app', type: 'product-filter' %}`}</span>
          </div>
        </div>
      </React.Fragment>
    );
  };

  renderContentOS20 = () => {
    const { i18n, themeId, pageType } = this.props;

    const step2Instruction =
      pageType === 'collection'
        ? i18n.translate('AdminHTML.ShopifyIntegration.Guide.collectionPageStep2_instruction', {
            t1: (
              <TextStyle variation="strong">
                {i18n.translate(
                  'AdminHTML.ShopifyIntegration.Guide.collectionPageStep2_instruction_t1'
                )}
              </TextStyle>
            ),
            t2: (
              <TextStyle variation="strong">
                {i18n.translate(
                  'AdminHTML.ShopifyIntegration.Guide.collectionPageStep2_instruction_t2'
                )}
              </TextStyle>
            ),
            t3: (
              <TextStyle variation="strong">
                {i18n.translate(
                  'AdminHTML.ShopifyIntegration.Guide.collectionPageStep1_instruction2_t2'
                )}
              </TextStyle>
            )
          })
        : i18n.translate('AdminHTML.ShopifyIntegration.Guide.searchPageStep2_instruction', {
            t1: (
              <TextStyle variation="strong">
                {i18n.translate(
                  'AdminHTML.ShopifyIntegration.Guide.searchPageStep2_instruction_t1'
                )}
              </TextStyle>
            ),
            t2: (
              <TextStyle variation="strong">
                {i18n.translate(
                  'AdminHTML.ShopifyIntegration.Guide.collectionPageStep1_instruction2_t2'
                )}
              </TextStyle>
            )
          });

    return (
      <React.Fragment>
        {/* Step 1 */}
        <div className="step">
          <div style={{ display: 'inline-flex' }}>
            <div className="left-col">
              <TextStyle variation="strong">
                {i18n.translate('AdminHTML.ShopifyIntegration.Guide.collectionPageStep1')}
              </TextStyle>

              <div style={{ padding: '16px 0 0' }}>
                {i18n.translate(
                  'AdminHTML.ShopifyIntegration.Guide.collectionPageStep1_instruction1',
                  {
                    t1: (
                      <TextStyle variation="strong">
                        {i18n.translate(
                          'AdminHTML.ShopifyIntegration.Guide.collectionPageStep1_instruction1_t1'
                        )}
                      </TextStyle>
                    ),
                    t2: (
                      <TextStyle>
                        {pageType === 'collection'
                          ? i18n.translate(
                              'AdminHTML.ShopifyIntegration.Guide.collectionPageStep1_instruction1_t2_collection'
                            )
                          : i18n.translate(
                              'AdminHTML.ShopifyIntegration.Guide.collectionPageStep1_instruction1_t2_search'
                            )}
                      </TextStyle>
                    )
                  }
                )}
              </div>

              <div style={{ padding: '0 0 16px' }}>
                {i18n.translate(
                  'AdminHTML.ShopifyIntegration.Guide.collectionPageStep1_instruction2',
                  {
                    t1: (
                      <TextStyle variation="strong">
                        {i18n.translate(
                          'AdminHTML.ShopifyIntegration.Guide.collectionPageStep1_instruction2_t1'
                        )}
                      </TextStyle>
                    ),
                    t2: (
                      <TextStyle variation="strong">
                        {i18n.translate(
                          'AdminHTML.ShopifyIntegration.Guide.collectionPageStep1_instruction2_t2'
                        )}
                      </TextStyle>
                    ),
                    t3: (
                      <TextStyle variation="strong">
                        {i18n.translate(
                          'AdminHTML.ShopifyIntegration.Guide.collectionPageStep1_instruction2_t3'
                        )}
                      </TextStyle>
                    ),
                    t4: (
                      <TextStyle variation="strong">
                        {pageType === 'collection'
                          ? i18n.translate(
                              'AdminHTML.ShopifyIntegration.Guide.collectionPageStep1_instruction2_t4_collection'
                            )
                          : i18n.translate(
                              'AdminHTML.ShopifyIntegration.Guide.collectionPageStep1_instruction2_t4_search'
                            )}
                      </TextStyle>
                    )
                  }
                )}
              </div>

              {/* Add widget */}
              <div>
                <Button
                  primary
                  onClick={() => {
                    const templatePage = pageType === 'collection' ? 'collection' : 'search';
                    navigateToAppBlockSSR(
                      themeId,
                      '/collections/frontpage',
                      SSR_APP_BLOCK.FILTER_PRODUCT_LIST_SSR,
                      templatePage
                    );
                  }}>
                  {i18n.translate('AdminHTML.ShopifyIntegration.Actions.addWidget')}
                </Button>
              </div>
            </div>
            {/* Illustration image */}
            <div className="right-col">
              <img
                className="thumbnail clickable"
                src={pageType === 'collection' ? GuideCollectionPageStep1 : GuideSearchPageStep1}
                width={500}
                height={281}
                alt=""
                onClick={() =>
                  window.open(
                    pageType === 'collection' ? GuideCollectionPageStep1 : GuideSearchPageStep1,
                    '_blank',
                    'noopener,noreferrer'
                  )
                }
              />
            </div>
          </div>
        </div>

        {/* Step 2 */}
        <div className="step">
          <div style={{ display: 'inline-flex' }}>
            <div className="left-col">
              <TextStyle variation="strong">
                {pageType === 'collection'
                  ? i18n.translate('AdminHTML.ShopifyIntegration.Guide.collectionPageStep2')
                  : i18n.translate('AdminHTML.ShopifyIntegration.Guide.searchPageStep2')}
              </TextStyle>

              <div style={{ padding: '16px 0' }}>{step2Instruction}</div>
              <div>
                {i18n.translate('AdminHTML.ShopifyIntegration.Guide.collectionPageStep2_save', {
                  t1: (
                    <TextStyle variation="strong">
                      {i18n.translate('AdminHTML.ShopifyIntegration.Actions.save')}
                    </TextStyle>
                  )
                })}
              </div>
            </div>
            {/* Illustration image */}
            <div className="right-col">
              <img
                className="thumbnail clickable"
                src={pageType === 'collection' ? GuideCollectionPageStep2 : GuideSearchPageStep2}
                width={500}
                height={281}
                alt=""
                onClick={() =>
                  window.open(
                    pageType === 'collection' ? GuideCollectionPageStep2 : GuideSearchPageStep2,
                    '_blank',
                    'noopener,noreferrer'
                  )
                }
              />
            </div>
          </div>
        </div>
      </React.Fragment>
    );
  };

  copyCodeSnippetClipBoardStep1 = () => {
    const { i18n } = this.props;
    const code = `
{% comment %}
Place original file code here
{% endcomment %}`;

    copyClipBoard(i18n, code);
  };

  copyCodeSnippetClipBoardStep2 = () => {
    const { i18n } = this.props;
    const code = `{% render 'boost-sd-app', type: 'product-filter' %}`;

    copyClipBoard(i18n, code);
  };
}

export default withI18n()(CollectionSearchManual);
